import {
	generateAccessName,
	AccessKey,
} from "../../../../../../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [AccessKey.ORDERS];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */
enum TabKeys {
	NEW_ORDER = "newOrder",
	EDIT_ORDER = "editOrder",
	REVERT_ORDER = "revertOrder",
	CLOSED_ORDER = "closeOrder",
	COPY_ORDER = "copyOrder",
	SET_ORDER_EXECUTOR = "setOrderExecutor",
	APPROVE_ORDER = "approveOrder",
	SEND_ORDER_AGAIN = "sendOrderAgain",
	RECALCULATE_ORDER = "recalculateOrder",
	RECALCULATE_ORDER_WITH_ACTUAL_RATES = "recalculateOrderWithActualRates",
	RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR = "returnOrderOnAirOrRemoveExecutor",
	SEND_ORDER_TO_AIR = "sendOrderToAir",
	SEND_ORDER_TO_EXECUTING = "sendOrderToExecuting",
	TOGGLE_ORDER_AIR = "toggleOrderAir",
	SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED = "setOrderCallToClientBeingRequired",
	SET_ORDER_CALL_TO_CLIENT_SUCCESS = "setOrderCallToClientSuccess",
	SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED = "setOrderCallToExecutorBeingRequired",
	SET_ORDER_CALL_TO_EXECUTOR_SUCCESS = "setOrderCallToExecutorSuccess",
	SHOW_CLIENT_NUMBER_TO_EXECUTOR = "showClientNumberToExecutor",
	COLOR_ORDER = "colorOrder",
	CALL_ORDER_CLIENT = "callOrderClient",
	CALL_ORDER_EXECUTOR = "callOrderExecutor",
	SMS_BUTTON = "SMSButton",
	AUTO_CALL_CLIENT_BUTTON = "AutoCallClientButton",
	ORDER_RETURN_FROM_TRANSFER = "orderReturnFromTransfer",
	ORDER_SEND_TO_TRANSFER = "orderSendToTransfer",
	ORDER_SEND_TO_TRANSFER_LOCAL = "orderSendToTransferLocal",
	ORDER_SEND_TO_TRANSFER_GLOBAL = "orderSendToTransferGlobal",
	ORDER_SEND_TO_TRANSFER_OUTSIDE = "orderSendToTransferOutside",
}

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.NEW_ORDER]: generateAccessSection(AccessKey.NEW_ORDER),
	[TabKeys.EDIT_ORDER]: generateAccessSection(AccessKey.EDIT_ORDER),
	[TabKeys.REVERT_ORDER]: generateAccessSection(AccessKey.REVERT_ORDER),
	[TabKeys.CLOSED_ORDER]: generateAccessSection(AccessKey.CLOSED_ORDER),
	[TabKeys.COPY_ORDER]: generateAccessSection(AccessKey.COPY_ORDER),
	[TabKeys.SET_ORDER_EXECUTOR]: generateAccessSection(
		AccessKey.SET_ORDER_EXECUTOR,
	),
	[TabKeys.APPROVE_ORDER]: generateAccessSection(AccessKey.APPROVE_ORDER),
	[TabKeys.SEND_ORDER_AGAIN]: generateAccessSection(
		AccessKey.SEND_ORDER_AGAIN,
	),
	[TabKeys.RECALCULATE_ORDER]: generateAccessSection(
		AccessKey.RECALCULATE_ORDER,
	),
	[TabKeys.RECALCULATE_ORDER_WITH_ACTUAL_RATES]: generateAccessSection(
		AccessKey.RECALCULATE_ORDER_WITH_ACTUAL_RATES,
	),
	[TabKeys.RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR]: generateAccessSection(
		AccessKey.RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR,
	),
	[TabKeys.SEND_ORDER_TO_AIR]: generateAccessSection(
		AccessKey.SEND_ORDER_TO_AIR,
	),
	[TabKeys.SEND_ORDER_TO_EXECUTING]: generateAccessSection(
		AccessKey.SEND_ORDER_TO_EXECUTING,
	),
	[TabKeys.TOGGLE_ORDER_AIR]: generateAccessSection(
		AccessKey.TOGGLE_ORDER_AIR,
	),
	[TabKeys.SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED]: generateAccessSection(
		AccessKey.SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED,
	),
	[TabKeys.SET_ORDER_CALL_TO_CLIENT_SUCCESS]: generateAccessSection(
		AccessKey.SET_ORDER_CALL_TO_CLIENT_SUCCESS,
	),
	[TabKeys.SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED]: generateAccessSection(
		AccessKey.SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED,
	),
	[TabKeys.SET_ORDER_CALL_TO_EXECUTOR_SUCCESS]: generateAccessSection(
		AccessKey.SET_ORDER_CALL_TO_EXECUTOR_SUCCESS,
	),
	[TabKeys.SHOW_CLIENT_NUMBER_TO_EXECUTOR]: generateAccessSection(
		AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR,
	),
	[TabKeys.COLOR_ORDER]: generateAccessSection(AccessKey.COLOR_ORDER),
	[TabKeys.CALL_ORDER_CLIENT]: generateAccessSection(
		AccessKey.CALL_ORDER_CLIENT,
	),
	[TabKeys.CALL_ORDER_EXECUTOR]: generateAccessSection(
		AccessKey.CALL_ORDER_EXECUTOR,
	),
	[TabKeys.SMS_BUTTON]: generateAccessSection(AccessKey.SMS_BUTTON),
	[TabKeys.AUTO_CALL_CLIENT_BUTTON]: generateAccessSection(
		AccessKey.AUTO_CALL_CLIENT_BUTTON,
	),
	[TabKeys.ORDER_RETURN_FROM_TRANSFER]: generateAccessSection(
		AccessKey.ORDER_RETURN_FROM_TRANSFER,
	),
	[TabKeys.ORDER_SEND_TO_TRANSFER]: generateAccessSection(
		AccessKey.ORDER_SEND_TO_TRANSFER,
	),
	[TabKeys.ORDER_SEND_TO_TRANSFER_LOCAL]: generateAccessSection(
		AccessKey.ORDER_SEND_TO_TRANSFER_LOCAL,
	),
	[TabKeys.ORDER_SEND_TO_TRANSFER_GLOBAL]: generateAccessSection(
		AccessKey.ORDER_SEND_TO_TRANSFER_GLOBAL,
	),
	[TabKeys.ORDER_SEND_TO_TRANSFER_OUTSIDE]: generateAccessSection(
		AccessKey.ORDER_SEND_TO_TRANSFER_OUTSIDE,
	),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY };
